import {
  EllipsedText,
  FlexColumn,
  FlexRow,
  getFileNameFromPath,
  isValidLatitude,
  isValidLongitude,
  LabeledData,
  MaterialIcon,
  StyledButton,
  useLoading,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { StyledDivider } from 'baseui/divider';
import { StyledLink } from 'baseui/link';
import { ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { MEDIA_FILE_TYPE_OPTIONS } from '../../constants/media';
import { useCamera } from '../../hooks/useCamera';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { getDownloadMediaUrlByKey } from '../../services/evidenceService';
import { evidenceMediaState } from '../../storage/evidences';
import { gptAlarmTypeNameState } from '../../storage/gptStates';
import { Vehicle } from '../../types/vehicle';
import { downloadBlob } from '../../utils/files';
import { getMediaTypeLabel } from '../../utils/media';
import VehicleModal from '../assets/modal/VehicleModal';
import MediaThumbnail from '../shared/MediaThumbnail';
import { StyledTooltip } from '../ui/StyledTooltip';
import EvidenceMediaHeader from './EvidenceMediaHeader';
import EvidenceMetadataDisplay from './EvidenceMetadataDisplay';
import EvidenceMediaFullScreenPreviewModal from './modal/EvidenceMediaFullScreenPreviewModal';

type Props = Readonly<{
  onClose: () => void;
}>;

function EvidenceMediaDetail({ onClose }: Props) {
  const { theme, classes, css } = useStyles();
  const [source, setSource] = useState('');
  const media = useRecoilValue(evidenceMediaState);
  const alarmType = useRecoilValue(gptAlarmTypeNameState(media?.alarmType));
  const labelWidth = '80px';
  const { formatDateFromTs } = useMoment();
  const { loading: downloading, startLoading: startDownloading, stopLoading: stopDownloading } = useLoading();
  const { openModal, closeModal, showModal } = useModal();
  const { showFailFeedback } = useFeedback();
  const { zoomTo } = useCamera();
  const hasValidCoords =
    !!media?.longitude && isValidLongitude(media.longitude) && !!media.latitude && isValidLatitude(media.latitude);

  useEffect(() => {
    if (hasValidCoords) {
      zoomTo({ lat: media.latitude!, lng: media.longitude! });
    }
  }, [hasValidCoords, zoomTo]);

  if (!media) return null;

  const vehicle = media.asset?.vehicle;

  const openFullScreen = (url: string) => {
    setSource(url);
  };
  const closeFullScreen = () => {
    setSource('');
  };

  const download = async () => {
    if (!media.key) return;
    startDownloading();
    const { file, error } = await getDownloadMediaUrlByKey(media.key, media.mediaOrigin);
    if (error) {
      showFailFeedback(error);
    } else if (file) {
      downloadBlob(file, getFileNameFromPath(media.key));
    }
    stopDownloading();
  };

  return (
    <FlexColumn
      gap={theme.sizing.scale400}
      classNames={css({ overflow: 'hidden' })}
    >
      <FlexRow classNames={classes.centeredBetween}>
        <EvidenceMediaHeader
          media={media}
          actions={
            <FlexRow
              centered
              gap={theme.sizing.scale200}
            >
              {vehicle && (
                <StyledTooltip
                  content="Información del vehículo"
                  showArrow={false}
                >
                  <Button
                    shape="circle"
                    kind="tertiary"
                    size="compact"
                    onClick={openModal}
                  >
                    <MaterialIcon
                      name="info"
                      size="mini"
                    />
                  </Button>
                </StyledTooltip>
              )}
              {hasValidCoords && (
                <StyledTooltip
                  content="Ubicación"
                  showArrow={false}
                >
                  <Button
                    shape="circle"
                    kind="tertiary"
                    size="compact"
                    onClick={() => zoomTo({ lat: media.latitude!, lng: media.longitude! })}
                  >
                    <MaterialIcon
                      name="location_searching"
                      size="mini"
                    />
                  </Button>
                </StyledTooltip>
              )}
              <StyledTooltip
                content="Cerrar detalle"
                showArrow={false}
              >
                <Button
                  shape="circle"
                  kind="tertiary"
                  size="compact"
                  onClick={onClose}
                >
                  <MaterialIcon
                    name="close"
                    size="mini"
                  />
                </Button>
              </StyledTooltip>
            </FlexRow>
          }
        />
      </FlexRow>
      <FlexColumn classNames={css({ overflowY: 'auto' })}>
        <FlexColumn gap={theme.sizing.scale200}>
          <LabeledData
            label={'Tipo:'}
            content={MEDIA_FILE_TYPE_OPTIONS.find((option) => option.id === media.fileType)?.label ?? '-'}
            labelWidth={labelWidth}
          />
          <LabeledData
            label={'Usuario:'}
            content={
              media.user ?? (
                <ParagraphSmall
                  margin={0}
                  color={theme.colors.contentSecondary}
                >
                  <i>Autogenerada por evento de alarma</i>
                </ParagraphSmall>
              )
            }
            labelWidth={labelWidth}
          />
          {alarmType && (
            <LabeledData
              label={'Alarma:'}
              content={alarmType}
              labelWidth={labelWidth}
            />
          )}
          <LabeledData
            label={'Fecha:'}
            content={formatDateFromTs(media.created)}
            labelWidth={labelWidth}
          />
          <LabeledData
            label={'Dirección:'}
            content={
              hasValidCoords && media.geoReference ? (
                <StyledLink
                  href={`https://maps.google.com/?q=${media.latitude},${media.longitude}`}
                  target="_blank"
                  style={{ color: theme.colors.contentSecondary, ...theme.typography.ParagraphSmall }}
                >
                  <EllipsedText
                    breakAll
                    maxLines={3}
                  >
                    {media.geoReference}
                  </EllipsedText>
                </StyledLink>
              ) : (
                (media.geoReference ?? '-')
              )
            }
            labelWidth={labelWidth}
          />
        </FlexColumn>
        <FlexColumn gap={theme.sizing.scale400}>
          <MediaThumbnail
            mediaKey={media.key}
            origin={media.mediaOrigin}
            onPreview={openFullScreen}
            minHeight={280}
            onlyPreview
          />
          <EvidenceMetadataDisplay
            metadata={media.metadata}
            url={media.key}
          />
          <StyledDivider style={{ width: '100%', margin: 0 }} />
          <StyledButton
            onClick={download}
            isLoading={downloading}
            kind="secondary"
            disabled={!media.key}
            full
          >
            Descargar {getMediaTypeLabel(media.key)}
          </StyledButton>
        </FlexColumn>
      </FlexColumn>
      {vehicle && (
        <VehicleModal
          onClose={closeModal}
          isOpen={showModal}
          label={media.asset?.label ?? '-'}
          vehicle={vehicle as Vehicle}
        />
      )}
      {source && (
        <EvidenceMediaFullScreenPreviewModal
          media={media}
          onClose={closeFullScreen}
          isOpen
          source={source}
        />
      )}
    </FlexColumn>
  );
}

export default EvidenceMediaDetail;
